// Entries of one data source (/datasource/entries) — the rows inside a source,
// one value per column of its schema. Param-less like /submissions: the source's
// identity rides in location.state and a stateless visit redirects back to
// /datasource at render time.
//
// Two queries feed it: details supplies the source's identity and column schema
// (the meta line, the table headers and the create-row form's fields), entries
// supplies the rows.
//
// Add column, Add row, Edit and Delete are live for BOTH axes. Row delete is a
// TRUE bulk endpoint (`{ entryIds: string[] }`) and so is column delete
// (`{ columnIds: string[] }`), so on each axis the single and multi paths
// converge on one call and one confirmation — as the list page's do.
//
// Import replaces this source's records from a CSV, validated against its OWN
// columns before the upload starts (DataSourceEntriesImportModal).
//
// Export is a QUEUED JOB, not a download — the response says it started, not
// where the file is, so runExport toasts and stops. Its three surfaces (the
// toolbar's everything, the bar's selection, the kebab's one row) differ only in
// cardinality and share that one handler. NOTHING on this page is coming-soon
// any more.
//
// Rows and COLUMNS are two selection axes, and they are mutually exclusive:
// ticking a column clears the row selection and vice versa, enforced in the
// setters rather than by an effect so the two can never both be non-empty even
// for a frame. The one selection bar switches which axis it acts on, and takes
// the info tint in column mode so the active axis is readable at a glance.
//
// Both column actions are live too: Edit reopens the Add-column modal in rename
// mode (PATCH :id/columns/:columnId takes `{ columnName }` only, so the type
// picker locks), and Delete is another true bulk endpoint (`{ columnIds }`).
// Column selection stays a plain Set where rows needed a Map — rows are paged,
// so a selected row leaves `items`, but columns all arrive in the one details
// response and are never paged, so an id always resolves against `dsColumns`.
//
// No search box: the endpoint takes no search key, and one that filters nothing
// is worse than none. Nothing is sortable either — no orderBy, and the design
// draws no chevrons.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useDeleteDataSourceColumnsMutation,
  useExportDataSourceMutation,
  useGetDataSourceDetailsQuery,
  type DataSourceColumn,
} from '@/api/endpoints/datasourceApi';
import {
  useDeleteDataSourceEntriesMutation,
  useGetDataSourceEntriesQuery,
  type DataSourceEntryListItem,
} from '@/api/endpoints/datasourceEntriesApi';
import {
  Button,
  Card,
  ConfirmModal,
  DataTable,
  Icon,
  IconButton,
  TableFooter,
} from '@/components/common';
import useModuleAccess, {
  useCanExportDataSources,
  useCanImportDataSources,
} from '@/hooks/useModuleAccess';
import useStickyTotal from '@/hooks/useStickyTotal';
import DataSourceEntryModal from './entries/DataSourceEntryModal';
import DataSourceColumnModal from './entries/DataSourceColumnModal';
import DataSourceEntriesImportModal from './entries/DataSourceEntriesImportModal';
import { useDataSourceEntryColumns } from './entries/dataSourceEntryColumns';

const DataSourceEntriesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Verb gates (hidden, not disabled, when missing). Add row → `create`;
  // column add/rename → `edit` (schema shape); column delete → `delete`
  // (destructive-action symmetry, by decision); Import/Export are the
  // module's own extras and ride neither create nor view.
  const { canCreate, canEdit, canDelete } = useModuleAccess('data_sources');
  const canImport = useCanImportDataSources();
  const canExport = useCanExportDataSources();
  const { dataSourceId, dataSourceName } = (location.state as {
    dataSourceId?: string;
    dataSourceName?: string;
  } | null) ?? { dataSourceId: undefined, dataSourceName: undefined };

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [entryModal, setEntryModal] = useState<{ entry?: DataSourceEntryListItem } | null>(null);
  // null = closed, `{}` = add new, a `column` = rename that one (the entryModal
  // shape, one state for both modes).
  const [columnModal, setColumnModal] = useState<{ column?: DataSourceColumn } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  // One pending delete per AXIS, kept apart on purpose: different endpoint,
  // different copy, and dropping a column is far more destructive than dropping
  // a row. `name` is set only when a single target was picked, so each title can
  // say which.
  const [confirmRowDelete, setConfirmRowDelete] = useState<{
    ids: string[];
    name?: string;
  } | null>(null);
  const [confirmColumnDelete, setConfirmColumnDelete] = useState<{
    ids: string[];
    name?: string;
  } | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
  // A Map, not a Set: selection persists across pages, so the bar's actions need
  // the row's own facts even once it is no longer loaded (the list page's idiom).
  const [selected, setSelected] = useState<Map<string, DataSourceEntryListItem>>(new Map());

  const { data: details } = useGetDataSourceDetailsQuery(dataSourceId ?? '', {
    skip: !dataSourceId,
  });
  const {
    data: entries,
    currentData: currentEntries,
    isLoading,
    isFetching,
    isError,
  } = useGetDataSourceEntriesQuery(
    { dataSource: dataSourceId ?? '', page, limit },
    { skip: !dataSourceId, refetchOnMountOrArgChange: true },
  );

  const [deleteEntries, { isLoading: deleting }] = useDeleteDataSourceEntriesMutation();
  const [deleteColumns, { isLoading: deletingColumns }] = useDeleteDataSourceColumnsMutation();
  const [exportDataSource, { isLoading: exporting }] = useExportDataSourceMutation();

  /**
   * One handler for all three export surfaces — the toolbar's "everything", the
   * bar's selection and the kebab's single row differ only in cardinality, the
   * same way the delete pair does.
   *
   * The body is built CONDITIONALLY rather than passing `entryIds: undefined`:
   * omitting the key is what tells the backend "all records", and the request is
   * encrypted on the way out, so that distinction must not depend on a
   * serializer dropping undefined values.
   *
   * The export is a queued job, not a download (the response says it started,
   * not where the file is), so this toasts and stops. The selection is left
   * alone — nothing about those rows changed.
   */
  const runExport = async (entryIds?: string[]) => {
    try {
      await exportDataSource(
        entryIds?.length ? { dataSourceId, entryIds } : { dataSourceId },
      ).unwrap();
      toast.success(
        entryIds?.length
          ? t('datasource_entries_export_started_selected', { count: entryIds.length })
          : t('datasource_entries_export_started_all'),
      );
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const dsColumns = details?.columns ?? [];
  // A row has no name of its own, so the FIRST column's value stands in when a
  // confirmation needs to say which row — it is what identifies the row on screen.
  const labelOf = (entry: DataSourceEntryListItem) =>
    dsColumns[0] ? entry.values[dsColumns[0].columnId] : undefined;

  // The column axis selects by id; the objects themselves live in `dsColumns`,
  // which is never paged, so an id from the selection always resolves.
  const columnOf = (columnId: string) => dsColumns.find((c) => c.columnId === columnId);

  // Ticking a column drops the row selection — the two axes are exclusive.
  const toggleColumn = (columnId: string) => {
    setSelected(new Map());
    setSelectedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      return next;
    });
  };

  const columns = useDataSourceEntryColumns({
    columns: dsColumns,
    selectedColumns,
    onToggleColumn: toggleColumn,
    // Header checkboxes select into the COLUMN bar (rename/delete) — without
    // either verb the selection would be inert, so they don't render at all.
    selectableColumns: canEdit || canDelete,
    onEdit: canEdit ? (entry) => setEntryModal({ entry }) : undefined,
    onExport: canExport ? (entry) => runExport([entry.id]) : undefined,
    onDelete: canDelete
      ? (entry) => setConfirmRowDelete({ ids: [entry.id], name: labelOf(entry) })
      : undefined,
  });
  const items = entries?.items ?? [];
  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentEntries?.total, page);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  const selectedIdSet = useMemo(() => new Set(selected.keys()), [selected]);
  const onlySelected = selected.size === 1 ? [...selected.values()][0] : null;

  const runRowDelete = async () => {
    if (!confirmRowDelete) return;
    const { ids, name } = confirmRowDelete;
    try {
      await deleteEntries({ entryIds: ids }).unwrap();
      toast.success(
        name
          ? t('datasource_entries_deleted', { name })
          : t('datasource_entries_bulk_deleted', { count: ids.length }),
      );
      setSelected((prev) => {
        const next = new Map(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      setConfirmRowDelete(null);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const runColumnDelete = async () => {
    if (!confirmColumnDelete) return;
    const { ids, name } = confirmColumnDelete;
    try {
      await deleteColumns({ id: dataSourceId ?? '', columnIds: ids }).unwrap();
      toast.success(
        name
          ? t('datasource_column_deleted', { name })
          : t('datasource_column_bulk_deleted', { count: ids.length }),
      );
      // Drop the deleted ids from the axis — they no longer exist, so leaving
      // them ticked would keep the bar acting on phantom columns.
      setSelectedColumns((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      setConfirmColumnDelete(null);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedColumns(new Set());
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const row = items.find((e) => e.id === id);
        if (row) next.set(id, row);
      }
      return next;
    });
  };

  const allSelected = items.length > 0 && items.every((e) => selected.has(e.id));
  const someSelected = !allSelected && items.some((e) => selected.has(e.id));
  const toggleSelectAll = () => {
    setSelectedColumns(new Set());
    setSelected((prev) => {
      const next = new Map(prev);
      if (allSelected) items.forEach((e) => next.delete(e.id));
      else items.forEach((e) => next.set(e.id, e));
      return next;
    });
  };

  // Exclusivity means these can never both be true, so the bar's two modes need
  // no precedence rule.
  const columnMode = selectedColumns.size > 0;
  // The single-column target, resolved to its object rather than left as an id:
  // Edit seeds the modal from it (name + the locked type) and the delete confirm
  // names it. Undefined for 0 or 2+, which is exactly the Edit gate.
  const onlyColumn = selectedColumns.size === 1 ? columnOf([...selectedColumns][0]) : undefined;

  // "Reference list · HSE.CHEM · 6 records · 5 columns" — the identity segments
  // come from details, so they are dropped until it lands rather than rendering
  // stray separators around blanks.
  const meta = [
    details?.dataSourceType,
    details?.externalId,
    t('datasource_entries_records', { count: total }),
    t('datasource_entries_columns', { count: dsColumns.length }),
  ]
    .filter(Boolean)
    .join(' · ');

  // No data source in the router state → nothing to list; go back before
  // anything paints. Render-time (not an effect) so no frame of the empty table
  // reaches the screen, and `replace` so Back doesn't return here to bounce
  // again — the JWTProtectedRoute idiom, as /submissions uses.
  if (!dataSourceId) return <Navigate to='/datasource' replace />;

  return (
    <div className='datasource-page'>
      <header className='datasource-page__header'>
        <div className='datasource-page__identity'>
          <IconButton
            className='datasource-page__back'
            icon='arrow-left'
            variant='outline'
            aria-label={t('datasource_entries_back')}
            onClick={() => navigate('/datasource')}
          />
          <div className='datasource-page__headings'>
            <h1 className='datasource-page__title'>
              {details?.schemaName ?? dataSourceName ?? t('datasource_entries_title')}
            </h1>
            <p className='datasource-page__subtitle'>{meta}</p>
          </div>
        </div>
        <div className='datasource-page__header-actions'>
          {canImport && (
            <Button variant='secondary' iconStart='upload' onClick={() => setImportOpen(true)}>
              {t('datasource_action_import')}
            </Button>
          )}
          {canEdit && (
            <Button variant='secondary' iconStart='columns-3' onClick={() => setColumnModal({})}>
              {t('datasource_column_new')}
            </Button>
          )}
          {/* A row is one value per column, so there is nothing to add until the
              schema has at least one — a capability disable that sits UNDER the
              verb gate, not instead of it. */}
          {canCreate && (
            <Button
              iconStart='plus'
              onClick={() => setEntryModal({})}
              disabled={dsColumns.length === 0}
            >
              {t('datasource_entries_new')}
            </Button>
          )}
        </div>
      </header>

      <div className='datasource-page__toolbar'>
        <span />
        <div className='datasource-page__toolbar-trail'>
          <span className='datasource-page__count'>
            {t('datasource_entries_count', { count: total })}
          </span>
          {/* No ids — the absent key is what asks for every record. Disabled on
              an empty file: there is nothing to queue a job for. `total` is the
              server-side count, not this page's slice, which is the right
              measure for "all" — and it also keeps the button inert until the
              first load lands. */}
          {canExport && (
            <Button
              variant='secondary'
              iconStart='download'
              onClick={() => runExport()}
              loading={exporting}
              disabled={total === 0}
            >
              {t('datasource_entries_export_all')}
            </Button>
          )}
        </div>
      </div>

      <Card padded={false} className='datasource-page__panel'>
        {/* One bar, two axes. Column mode takes the info tint: the bar occupies
            the same slot either way, and the count wording alone is easy to skim
            past when deciding what a click is about to act on. */}
        {(selected.size > 0 || columnMode) && (
          <div
            className={[
              'datasource-page__selection-bar',
              columnMode && 'datasource-page__selection-bar--columns',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className='datasource-page__selection-lead'>
              <span className='datasource-page__selected'>
                {columnMode
                  ? t('datasource_entries_columns_selected_count', {
                      count: selectedColumns.size,
                    })
                  : t('datasource_entries_selected_count', { count: selected.size })}
              </span>
              <span className='datasource-page__selection-divider' />

              {/* Both axes share the shape — Edit at one, Delete at many. Rows
                  additionally offer Export; columns have no export. */}
              {columnMode ? (
                <>
                  {onlyColumn && canEdit && (
                    <Button
                      variant='ghost'
                      iconStart='square-pen'
                      onClick={() => setColumnModal({ column: onlyColumn })}
                    >
                      {t('datasource_column_action_edit')}
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant='ghost'
                      iconStart='trash-2'
                      className='datasource-page__selection-delete'
                      onClick={() =>
                        setConfirmColumnDelete({
                          ids: [...selectedColumns],
                          name: onlyColumn?.columnName,
                        })
                      }
                    >
                      {t('button_delete')}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {onlySelected && canEdit && (
                    <Button
                      variant='ghost'
                      iconStart='square-pen'
                      onClick={() => setEntryModal({ entry: onlySelected })}
                    >
                      {t('datasource_entries_action_edit')}
                    </Button>
                  )}
                  {canExport && (
                    <Button
                      variant='ghost'
                      iconStart='download'
                      onClick={() => runExport([...selected.keys()])}
                      loading={exporting}
                    >
                      {t('datasource_action_export')}
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant='ghost'
                      iconStart='trash-2'
                      className='datasource-page__selection-delete'
                      onClick={() =>
                        setConfirmRowDelete({
                          ids: [...selected.keys()],
                          name: onlySelected ? labelOf(onlySelected) : undefined,
                        })
                      }
                    >
                      {t('button_delete')}
                    </Button>
                  )}
                </>
              )}
            </div>
            <Button
              variant='ghost'
              onClick={() => (columnMode ? setSelectedColumns(new Set()) : setSelected(new Map()))}
            >
              {t('button_clear')}
            </Button>
          </div>
        )}

        <DataTable
          columns={columns}
          rows={items}
          rowKey={(e) => e.id}
          // Row selection feeds the row bar (edit/export/delete) — without all
          // three verbs it would select into a bar of just count + Clear.
          selectable={canEdit || canExport || canDelete}
          selectedIds={selectedIdSet}
          onToggleSelect={toggleSelect}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleSelectAll={toggleSelectAll}
          selectAllLabel={t('datasource_entries_select_all')}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          empty={
            <>
              <Icon name='table-2' size={26} />
              {t('datasource_entries_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('datasource_entries_items_per_page')}
          rangeText={t('datasource_entries_range', { from, to, total })}
        />
      </Card>

      {/* Keyed on the row so a switch between two rows re-seeds the draft —
          the modal only re-seeds on `open`, which never flips in that case.
          The keys are PREFIXED because these two modals are siblings: both fall
          back to a create-mode literal when closed, and a bare 'new' on each
          collides. */}
      <DataSourceEntryModal
        key={`entry-${entryModal?.entry?.id ?? 'new'}`}
        open={!!entryModal}
        onClose={() => setEntryModal(null)}
        dataSourceId={dataSourceId}
        columns={dsColumns}
        entry={entryModal?.entry}
      />
      {/* Keyed on the target for the same reason the entry modal is: `open`
          never flips when switching straight from one column's Edit to another. */}
      <DataSourceColumnModal
        key={`column-${columnModal?.column?.columnId ?? 'new'}`}
        open={!!columnModal}
        onClose={() => setColumnModal(null)}
        dataSourceId={dataSourceId}
        column={columnModal?.column}
      />

      {/* Mounted only while open, so a picked file is discarded on close rather
          than needing a reset effect. */}
      {importOpen && (
        <DataSourceEntriesImportModal
          onClose={() => setImportOpen(false)}
          dataSourceId={dataSourceId}
          dataSourceName={details?.schemaName ?? dataSourceName ?? ''}
          columns={dsColumns}
        />
      )}

      {/* One modal for both paths — the endpoint is bulk, so a single row and a
          whole selection are the same call; only the title differs. */}
      {confirmRowDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmRowDelete(null)}
          onConfirm={runRowDelete}
          tone='danger'
          icon='trash-2'
          title={
            confirmRowDelete.name
              ? t('datasource_entries_delete_confirm_title', { name: confirmRowDelete.name })
              : t('datasource_entries_bulk_delete_title', { count: confirmRowDelete.ids.length })
          }
          description={t('datasource_entries_delete_confirm_body')}
          confirmLabel={t('button_delete')}
          confirmVariant='danger'
          loading={deleting}
        />
      )}

      {/* The column axis's own confirmation. Its copy is deliberately blunter
          than the row one's: dropping a column also destroys that column's
          value in EVERY row of the file, which the count alone doesn't convey. */}
      {confirmColumnDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmColumnDelete(null)}
          onConfirm={runColumnDelete}
          tone='danger'
          icon='trash-2'
          title={
            confirmColumnDelete.name
              ? t('datasource_column_delete_confirm_title', { name: confirmColumnDelete.name })
              : t('datasource_column_bulk_delete_title', {
                  count: confirmColumnDelete.ids.length,
                })
          }
          description={t('datasource_column_delete_confirm_body', {
            count: confirmColumnDelete.ids.length,
          })}
          confirmLabel={t('button_delete')}
          confirmVariant='danger'
          loading={deletingColumns}
        />
      )}
    </div>
  );
};

export default DataSourceEntriesPage;
